import { useField, useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { LakehouseConnectionTypeEnum, LakehouseMySqlConnection, MySqlSslMode } from '@vendia/management-api-types'
import debug from 'debug'
import { useNavigate } from 'react-router'
import Card from 'src/components/containers/card'
import ListboxField from 'src/components/fields/listbox.field'
import NumberField from 'src/components/fields/number.field'
import TextField from 'src/components/fields/text.field'
import { ScrollableStepContent } from 'src/components/flows/scrollable-step-content'
import StepButtons from 'src/components/flows/step-buttons'
import { StepContentHeader } from 'src/components/flows/step-header'
import { StepWrapper } from 'src/components/flows/step-wrapper'
import { StepComponent } from 'src/components/flows/types'
import { createOnChangeValidator } from 'src/utils/form/validation'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { useFirstFocus } from 'src/utils/hooks/use-first-focus'
import { LakehouseFlowStepValues } from 'src/utils/lakehouse/types'
import { lakehouseFlowFieldValidator } from 'src/utils/lakehouse/validators'
import notify from 'src/utils/notify'

import { StepId } from '../config'
import { getOltpTables } from '../utils'

const logger = debug('app:MySql')

// Map connection string sslmode to MySqlSslMode enum
const sslModeMap: Record<string, MySqlSslMode> = {
  disable: MySqlSslMode.Disable,
  prefer: MySqlSslMode.Prefer,
  require: MySqlSslMode.Require,
  verifyca: MySqlSslMode.VerifyCa,
  verifyfull: MySqlSslMode.VerifyIdentity,
}

const sslModeOptions = [
  { value: MySqlSslMode.Disable, label: 'Disable SSL' },
  { value: MySqlSslMode.Prefer, label: 'Prefer SSL (fallback to no SSL)' },
  { value: MySqlSslMode.Require, label: 'Require SSL (no cert validation)' },
  { value: MySqlSslMode.VerifyCa, label: 'Verify CA Certificate' },
  { value: MySqlSslMode.VerifyIdentity, label: 'Verify CA Certificate and Hostname' },
]

export const StepConnectMySql: StepComponent<LakehouseFlowStepValues> = ({ context }) => {
  let form = context.form
  const client = useControlPlaneApi()
  const navigate = useNavigate()
  const nameRef = useFirstFocus()
  const prefix = 'source.mysql'

  const { uniName, nodeName } = useStore(form.store, (s) => s.values)
  const connection = useStore(form.store, (s) => s.values.source.connection)

  useField({ form, name: `${prefix}.ssl`, defaultValue: MySqlSslMode.Disable })
  useField({ form, name: `${prefix}.port`, defaultValue: 3306 })
  useField({ form, name: 'source.mysqlConnectionString', defaultValue: '' })

  const mysqlConnectionDetails = useStore(form.store, (s) => s.values.source?.mysql)
  const connectionStringValue = useStore(form.store, (s) => s.values.source?.mysqlConnectionString)

  const getTablesMutation = useMutation({
    mutationFn: async () => {
      if (!uniName) {
        throw new Error('Uni name is required.')
      }
      if (!nodeName) {
        throw new Error('Node name is required.')
      }
      if (
        !connectionStringValue &&
        (!mysqlConnectionDetails?.host ||
          !mysqlConnectionDetails?.port ||
          !mysqlConnectionDetails?.database ||
          !mysqlConnectionDetails?.username ||
          !mysqlConnectionDetails?.password)
      ) {
        throw new Error('All connection details are required when connection string is not provided.')
      }
      const tables = await getOltpTables(client, uniName, nodeName, {
        connectionStringValue: connectionStringValue ?? '',
        connectionDetails: mysqlConnectionDetails,
        sslModeMap,
        form,
        fieldPrefix: 'source.mysql',
        defaultPort: 3306,
        requiredFields: ['host', 'port', 'database', 'username', 'password'] as (keyof LakehouseMySqlConnection)[],
        connectionType: LakehouseConnectionTypeEnum.Mysql,
      })

      return tables
    },
    onError: (error) => {
      if (error.message?.includes('connection')) {
        notify.error(`Connection error: ${error.message}. Please check your credentials and network connection.`)
      } else if (error.message?.includes('schema')) {
        notify.error(`Schema error: ${error.message}. Please verify the schema exists in your database.`)
      } else {
        notify.error(`Error testing MySQL connection: ${error.message}`)
      }
    },
    onSuccess: (response) => {
      logger('mysql connection response', response)
      if (response.errors) {
        const errorMessage = response.errors[0].message

        if (errorMessage.includes('schema')) {
          notify.error(`Schema error: ${errorMessage}. Please check that the schema exists and you have access to it.`)
        } else if (errorMessage.includes('access') || errorMessage.includes('permission')) {
          notify.error(
            `Permission error: ${errorMessage}. Please check that your database user has the necessary permissions.`,
          )
        } else if (errorMessage.includes('connection')) {
          notify.error(`Connection error: ${errorMessage}. Please check your connection details.`)
        } else {
          notify.error(`${errorMessage}`)
        }
        return
      }

      if (!response.getLakehouseTables) {
        notify.error('Error testing MySQL connection')
        return
      }

      if (response.getLakehouseTables.tables.length === 0) {
        notify.error('No tables found in the specified schema. Please verify your schema name and permissions.')
      }

      form.setFieldValue('source.availableTables', response.getLakehouseTables.tables as string[])
      form.setFieldValue('source.sourceRegion', response.getLakehouseTables.region as string)
      context.goToStep(StepId.SelectTable)
    },
  })

  return (
    <StepWrapper>
      <StepContentHeader centered large title='Connect to MySQL' description="Let's authenticate your MySQL database" />
      <ScrollableStepContent inset autoHeight>
        <Card padding='xl' className='mx-8 w-full max-w-4xl'>
          <TextField
            ref={nameRef}
            name='connectionName'
            label='Name'
            description='Easily identify and reuse this connection to ingest additional tables.'
            form={form}
            useNestedLabel
            maxLength={64}
            validators={createOnChangeValidator(
              {
                pattern: /^(?!\s*$).{1,64}$/,
                message: 'The connection name must be between 1 and 64 characters long.',
              },
              true,
            )}
          />

          <div className='flex w-full flex-col gap-4 pt-6'>
            <TextField
              name={'source.mysqlConnectionString'}
              label='Connection String (URI) - Optional'
              description={
                <span>
                  Paste your MySQL connection URI.{' '}
                  <strong>If provided, this will be used instead of the individual fields below.</strong> Example:{' '}
                  <code>mysql://user:password@host:port/database?ssl-mode=REQUIRED</code>.{' '}
                  <a
                    href='https://dev.mysql.com/doc/refman/8.0/en/connecting.html'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-link font-semibold'
                  >
                    Learn more
                  </a>
                  .
                </span>
              }
              form={form}
              useNestedLabel
              placeholder='mysql://...'
            />
          </div>

          <div className='relative flex items-center gap-6 py-6'>
            <div className='flex-grow border-t border-slate-200' />
            <span className='h6'>Or Enter Details Individually</span>
            <div className='flex-grow border-t border-slate-200' />
          </div>

          <div className='flex w-full flex-col gap-4'>
            <TextField
              name={`${prefix}.host`}
              label='Host'
              description='The hostname of your MySQL server'
              form={form}
              useNestedLabel
              validators={lakehouseFlowFieldValidator({
                stepId: StepId.ConnectMySql,
                listenTo: ['source.mysqlConnectionString'],
                validator: (value) => {
                  if (connection === LakehouseConnectionTypeEnum.Mysql && !connectionStringValue) {
                    if (!value) {
                      return 'Host is required when connection string is not provided.'
                    }
                  }
                  return undefined
                },
              })}
            />
            <NumberField
              name={`${prefix}.port`}
              label='Port'
              description='The port your MySQL server is listening on'
              form={form}
              useNestedLabel
              validators={lakehouseFlowFieldValidator({
                stepId: StepId.ConnectMySql,
                listenTo: ['source.mysqlConnectionString'],
                validator: (value) => {
                  if (connection === LakehouseConnectionTypeEnum.Mysql && !connectionStringValue) {
                    const fieldValue = value?.value ?? value
                    if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
                      return 'Port is required when connection string is not provided.'
                    }
                    const num = Number(fieldValue)
                    if (isNaN(num) || !Number.isInteger(num) || num <= 0 || num > 65535) {
                      return 'Port must be a valid integer between 1 and 65535.'
                    }
                  }
                  return undefined
                },
              })}
            />
            <TextField
              name={`${prefix}.database`}
              label='Database'
              description='The database containing the tables you want to share'
              form={form}
              useNestedLabel
              validators={lakehouseFlowFieldValidator({
                stepId: StepId.ConnectMySql,
                listenTo: ['source.mysqlConnectionString'],
                validator: (value) => {
                  if (connection === LakehouseConnectionTypeEnum.Mysql && !connectionStringValue) {
                    if (!value) {
                      return 'Database is required when connection string is not provided.'
                    }
                  }
                  return undefined
                },
              })}
            />
            <TextField
              name={`${prefix}.username`}
              label='Username'
              description='The username for the MySQL account you want to connect with'
              form={form}
              useNestedLabel
              validators={lakehouseFlowFieldValidator({
                stepId: StepId.ConnectMySql,
                listenTo: ['source.mysqlConnectionString'],
                validator: (value) => {
                  if (connection === LakehouseConnectionTypeEnum.Mysql && !connectionStringValue) {
                    if (!value) {
                      return 'Username is required when connection string is not provided.'
                    }
                  }
                  return undefined
                },
              })}
            />
            <TextField
              name={`${prefix}.password`}
              label='Password'
              description='The password for the MySQL account you want to connect with'
              form={form}
              type='password'
              useNestedLabel
              validators={lakehouseFlowFieldValidator({
                stepId: StepId.ConnectMySql,
                listenTo: ['source.mysqlConnectionString'],
                validator: (value) => {
                  if (connection === LakehouseConnectionTypeEnum.Mysql && !connectionStringValue) {
                    if (!value) {
                      return 'Password is required when connection string is not provided.'
                    }
                  }
                  return undefined
                },
              })}
            />
            <ListboxField
              name={`${prefix}.ssl`}
              label='SSL Mode'
              description='Select the SSL/TLS encryption mode for the connection'
              options={sslModeOptions}
              form={form}
              disabled={!!connectionStringValue}
              useNestedLabel
            />
          </div>
        </Card>
      </ScrollableStepContent>
      <form.Subscribe selector={(state) => [state.canSubmit, state.isPristine]}>
        {([canSubmit, isPristine]) => (
          <StepButtons
            context={context}
            isSubmitting={getTablesMutation.isPending}
            nextBlocked={getTablesMutation.isPending || !canSubmit || isPristine}
            hasCancel={true}
            onCancel={() => navigate('../', { relative: 'path' })}
            onNext={() => getTablesMutation.mutate()}
          />
        )}
      </form.Subscribe>
    </StepWrapper>
  )
}
